<script setup lang="ts">
import { useQuery } from '@tanstack/vue-query';
import { computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { EquipmentService } from '@/services/equipment.services';
import EquipmentSelect from './EquipmentSelect.vue';

const props = defineProps<{
  storeId: string;
  allowAll?: boolean;
  disabled?: boolean;
}>();
const model = defineModel<string>({ required: true });

const { t } = useI18n({ useScope: 'global' });

// Les sites du commerce choisi ; sans commerce, la liste reste vide plutôt que de montrer ceux des autres.
const { data } = useQuery({
  queryKey: computed(() => ['equipment', 'site-options', props.storeId]),
  queryFn: () =>
    EquipmentService.searchSites({
      page: 1,
      pageSize: 200,
      storeIds: [props.storeId],
      sortBy: 'name',
      sortOrder: 'ASC',
    }),
  enabled: computed(() => !!props.storeId),
});

// Un commerce n'a qu'un site : inutile de le faire choisir. Les listes qui dépendent du site se remplissent d'elles-mêmes.
watch(
  () => data.value?.data,
  (sites) => {
    if (props.allowAll || props.disabled || model.value) return;
    if (props.storeId && sites?.length === 1) model.value = sites[0].id;
  },
  { immediate: true },
);

const options = computed(() => [
  ...(props.allowAll ? [{ value: '', label: t('equipment.allSites') }] : []),
  ...(props.storeId ? (data.value?.data ?? []) : []).map((site) => ({
    value: site.id,
    label: site.name,
  })),
]);
</script>

<template>
  <EquipmentSelect
    v-model="model"
    :options="options"
    :placeholder="t('equipment.chooseSite')"
    :disabled="disabled || !storeId"
    :aria-label="t('equipment.fields.site')"
    icon="pi-map-marker"
  />
</template>
